/**
 * Interface font, code font and text size.
 *
 * The interface font sets `--font`, the code font `--mono` (ids, versions, model slugs, logs),
 * and the text size scales every type token and fixed type size through `--type-scale`, so a
 * size names the body text and the rest of the type scale keeps its proportions. The
 * interface scale (lib/display.ts) still zooms the whole page on top of this.
 *
 * Fonts marked bundled ship with the dashboard (styles/fonts.css); the others are installed
 * on Macs and Windows PCs alike. Like the colour theme, the choice is per browser: it belongs
 * to the monitor and the eyes in front of it, not to the account.
 */

import { useEffect, useState } from 'react';

const FONT_KEY = 'nightshift.font';
const CODE_FONT_KEY = 'nightshift.code-font';
const TEXT_SIZE_KEY = 'nightshift.text-size';
const TYPOGRAPHY_EVENT = 'nightshift-typography';

export interface FontChoice {
  id: string;
  name: string;
  description: string;
  /** The `font-family` list written into the token. */
  stack: string;
}

/** The fallbacks after every interface font; also `--font` in tokens.css after Inter. */
const UI_FALLBACK = "system-ui, -apple-system, 'Segoe UI', sans-serif";
/** `--mono` in tokens.css, and the fallbacks after every code font. */
const MONO_FALLBACK = "ui-monospace, 'SFMono-Regular', 'Cascadia Mono', Menlo, Consolas, monospace";

export const UI_FONTS: readonly FontChoice[] = [
  { id: 'inter', name: 'Inter', description: 'The original', stack: `'Inter', ${UI_FALLBACK}` },
  { id: 'system', name: 'System', description: 'San Francisco on a Mac, Segoe UI on Windows', stack: UI_FALLBACK },
  {
    id: 'atkinson',
    name: 'Atkinson Hyperlegible',
    description: 'Drawn so similar letters cannot be confused',
    stack: `'Atkinson Hyperlegible Next', 'Inter', ${UI_FALLBACK}`,
  },
  {
    id: 'verdana',
    name: 'Verdana',
    description: 'Wide and open, designed for small text on screen',
    stack: `Verdana, 'DejaVu Sans', 'Inter', ${UI_FALLBACK}`,
  },
  { id: 'lexend', name: 'Lexend', description: 'Wide letter spacing for easier reading', stack: `'Lexend', 'Inter', ${UI_FALLBACK}` },
  { id: 'ibm-plex-sans', name: 'IBM Plex Sans', description: 'Plain and even at small sizes', stack: `'IBM Plex Sans', 'Inter', ${UI_FALLBACK}` },
  { id: 'source-sans', name: 'Source Sans 3', description: 'Narrow, fits more on a line', stack: `'Source Sans 3', 'Inter', ${UI_FALLBACK}` },
];

export const CODE_FONTS: readonly FontChoice[] = [
  { id: 'system-mono', name: 'System monospace', description: 'SF Mono on a Mac, Cascadia Mono on Windows', stack: MONO_FALLBACK },
  { id: 'jetbrains-mono', name: 'JetBrains Mono', description: 'Tall letters; 0 and O, 1 and l differ', stack: `'JetBrains Mono', ${MONO_FALLBACK}` },
  {
    id: 'atkinson-mono',
    name: 'Atkinson Hyperlegible Mono',
    description: 'The legibility face, monospaced',
    stack: `'Atkinson Hyperlegible Mono', ${MONO_FALLBACK}`,
  },
  { id: 'ibm-plex-mono', name: 'IBM Plex Mono', description: 'Matches IBM Plex Sans', stack: `'IBM Plex Mono', ${MONO_FALLBACK}` },
];

/** Body text sizes offered, in CSS pixels before the interface scale. */
export const TEXT_SIZES = [12, 12.5, 13, 14, 15, 16, 18] as const;

/** `--fs-body` in tokens.css: the size every other type size is proportioned against. */
export const DEFAULT_TEXT_SIZE = 12.5;
const DEFAULT_FONT_ID = 'inter';
const DEFAULT_CODE_FONT_ID = 'system-mono';

export interface Typography {
  fontId: string;
  codeFontId: string;
  /** Body text size in pixels, one of `TEXT_SIZES`. */
  textSize: number;
}

export function fontById(id: string): FontChoice {
  return UI_FONTS.find((font) => font.id === id) ?? UI_FONTS[0]!;
}

export function codeFontById(id: string): FontChoice {
  return CODE_FONTS.find((font) => font.id === id) ?? CODE_FONTS[0]!;
}

export function textSizeOrDefault(size: number): number {
  return (TEXT_SIZES as readonly number[]).includes(size) ? size : DEFAULT_TEXT_SIZE;
}

/** The token values for a choice; `null` means "leave the tokens.css default". */
export function typographyVariables(current: Typography): Record<'--font' | '--mono' | '--type-scale', string | null> {
  const font = fontById(current.fontId);
  const code = codeFontById(current.codeFontId);
  const size = textSizeOrDefault(current.textSize);
  return {
    '--font': font.id === DEFAULT_FONT_ID ? null : font.stack,
    '--mono': code.id === DEFAULT_CODE_FONT_ID ? null : code.stack,
    '--type-scale': size === DEFAULT_TEXT_SIZE ? null : String(Math.round((size / DEFAULT_TEXT_SIZE) * 1000) / 1000),
  };
}

/* ── persistence ───────────────────────────────────────────────────────────────────── */

function typography(): Typography {
  try {
    return {
      fontId: fontById(window.localStorage.getItem(FONT_KEY) ?? DEFAULT_FONT_ID).id,
      codeFontId: codeFontById(window.localStorage.getItem(CODE_FONT_KEY) ?? DEFAULT_CODE_FONT_ID).id,
      textSize: textSizeOrDefault(Number(window.localStorage.getItem(TEXT_SIZE_KEY) ?? DEFAULT_TEXT_SIZE)),
    };
  } catch {
    return { fontId: DEFAULT_FONT_ID, codeFontId: DEFAULT_CODE_FONT_ID, textSize: DEFAULT_TEXT_SIZE };
  }
}

export function setTypography(next: Typography): void {
  const resolved: Typography = {
    fontId: fontById(next.fontId).id,
    codeFontId: codeFontById(next.codeFontId).id,
    textSize: textSizeOrDefault(next.textSize),
  };
  try {
    window.localStorage.setItem(FONT_KEY, resolved.fontId);
    window.localStorage.setItem(CODE_FONT_KEY, resolved.codeFontId);
    window.localStorage.setItem(TEXT_SIZE_KEY, String(resolved.textSize));
  } catch {
    // Applies for this page lifetime; it simply will not persist.
  }
  applyTypography(resolved);
  window.dispatchEvent(new Event(TYPOGRAPHY_EVENT));
}

/** Write the tokens onto the document root, clearing any that are back at their default. */
export function applyTypography(current: Typography = typography()): void {
  const root = document.documentElement;
  for (const [name, value] of Object.entries(typographyVariables(current))) {
    if (value === null) root.style.removeProperty(name);
    else root.style.setProperty(name, value);
  }
}

export function useTypography(): Typography {
  const [current, setCurrent] = useState(typography);
  useEffect(() => {
    const sync = (): void => setCurrent(typography());
    // Another tab of this browser changed it: repaint this one too.
    const fromStorage = (event: StorageEvent): void => {
      if (event.key !== FONT_KEY && event.key !== CODE_FONT_KEY && event.key !== TEXT_SIZE_KEY) return;
      applyTypography();
      sync();
    };
    window.addEventListener(TYPOGRAPHY_EVENT, sync);
    window.addEventListener('storage', fromStorage);
    return () => {
      window.removeEventListener(TYPOGRAPHY_EVENT, sync);
      window.removeEventListener('storage', fromStorage);
    };
  }, []);
  return current;
}
